import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  isEmptyValue,
  cellValue,
  updateProperty,
  type DatabaseSchema,
  type PropertyDef,
} from '@nexus/core';
import { saveSchema, setRowValue } from '@nexus/workspace';
import { useStore } from '../../db/context';
import { useDatabase } from '../../db/database';
import { TYPE_ICON } from './icons';
import { PropertyValue } from './values';

/**
 * The properties of a database row, as the database defines them: in groups
 * ("Overzicht", "Details"), empty ones folded away like in Notion.
 */
export function RowProperties({
  pageId,
  props,
  db,
  readOnly,
}: {
  pageId: string;
  props: Record<string, unknown>;
  db: { id: string; schema: DatabaseSchema };
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const store = useStore();
  const [showEmpty, setShowEmpty] = useState(false);
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const { schema } = db;
  // The loaded database has the computed values (rollups, formulas, backlinks).
  const loaded = useDatabase(db.id);
  const current = loaded?.rows.find((r) => r.id === pageId) ?? { title: '', props };
  const valueOf = (p: PropertyDef) => cellValue(p, { ...current, props });
  const filled = (p: PropertyDef) => p.type === 'button' || !isEmptyValue(valueOf(p));
  const emptyCount = schema.properties.filter((p) => !filled(p)).length;
  const visible = schema.properties.filter((p) => showEmpty || filled(p));

  // Groups in the order they first appear; ungrouped properties first.
  const groups: [string, PropertyDef[]][] = [];
  for (const p of visible) {
    const name = p.group ?? '';
    const g = groups.find(([n]) => n === name);
    if (g) g[1].push(p);
    else groups.push([name, [p]]);
  }
  groups.sort(([a], [b]) => (a === '' ? -1 : b === '' ? 1 : 0));

  const row = (def: PropertyDef) => {
    const Icon = TYPE_ICON[def.type];
    return (
      <div key={def.id} className="contents" data-testid="row-property">
        <dt className="flex items-center gap-1.5 py-0.5 text-fg-muted">
          <Icon className="size-3.5 shrink-0" aria-hidden />
          <span className="truncate">{def.name}</span>
        </dt>
        <dd className="flex min-w-0 items-center">
          <PropertyValue
            def={def}
            value={valueOf(def)}
            rowId={pageId}
            related={loaded?.related}
            readOnly={readOnly}
            onChange={(v) => void setRowValue(store, pageId, def, v)}
            onDefChange={(next) => void saveSchema(store, db.id, updateProperty(schema, next))}
          />
        </dd>
      </div>
    );
  };

  return (
    <div className="mb-2 flex flex-col gap-2">
      {groups.map(([name, defs]) => (
        <div key={name || '__'}>
          {name && (
            <button
              type="button"
              aria-expanded={!closed.has(name)}
              onClick={() =>
                setClosed((c) => {
                  const next = new Set(c);
                  if (next.has(name)) next.delete(name);
                  else next.add(name);
                  return next;
                })
              }
              className="mb-0.5 flex items-center gap-1 rounded px-1 text-xs font-medium text-fg-muted hover:bg-bg-hover"
            >
              {closed.has(name) ? (
                <ChevronRight className="size-3" />
              ) : (
                <ChevronDown className="size-3" />
              )}
              {name}
            </button>
          )}
          {!closed.has(name) && (
            <dl className="grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-2 gap-y-0.5">
              {defs.map(row)}
            </dl>
          )}
        </div>
      ))}
      {emptyCount > 0 && (
        <button
          type="button"
          onClick={() => setShowEmpty((v) => !v)}
          className="flex items-center gap-1 self-start rounded px-1 py-0.5 text-xs text-fg-muted hover:bg-bg-hover hover:text-fg"
        >
          {showEmpty ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
          {showEmpty ? t('db.hideEmpty') : t('db.showEmpty', { count: emptyCount })}
        </button>
      )}
    </div>
  );
}
